import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Plus, X } from "lucide-react";
import { GALLERY } from "@/data/content";
import { track } from "@/lib/analytics";

const FILTERS = ["All", "Live Grill", "The Rig", "From the Garden", "The Table"];

export default function Gallery() {
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const items = useMemo(
    () => (filter === "All" ? GALLERY : GALLERY.filter((g) => g.category === filter)),
    [filter]
  );

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((c) => (c + 1) % items.length);
      if (e.key === "ArrowLeft") setLightbox((c) => (c - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, items.length]);

  const current = lightbox !== null ? items[lightbox] : null;

  return (
    <section id="gallery" className="px-6 pb-24 pt-8 sm:px-10 lg:px-16 lg:pb-32 lg:pt-10">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="mb-12 flex flex-wrap items-end justify-between gap-6"
      >
        <div>
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.35em] text-amber-500/90">
            The Proof Is in the Fire
          </p>
          <h2 className="font-serif text-2xl uppercase tracking-tight text-amber-100 sm:text-3xl lg:text-4xl">
            From the Flame
          </h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={f === "All" ? "gallery-filter-all" : `gallery-filter-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => setFilter(f) || track("filter_click", { filter: f })}
              className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                filter === f
                  ? "border-ember bg-ember/15 text-amber-200"
                  : "border-neutral-800 text-neutral-500 hover:border-amber-500/40 hover:text-amber-300"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </motion.div>

      <motion.div layout className="grid grid-cols-12 gap-4">
        <AnimatePresence mode="popLayout">
          {items.map((item, i) => (
            <motion.button
              layout
              key={item.id}
              data-testid="gallery-item-card"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
              onClick={() => setLightbox(i) || track("gallery_open", { photo: item.title })}
              className={`group relative h-64 overflow-hidden border border-neutral-800/70 bg-hearth text-left sm:h-80 lg:h-96 ${item.span}`}
            >
              <div className="h-full w-full overflow-hidden">
                <img
                  src={item.url}
                  alt={item.title}
                  loading="lazy"
                  style={{ objectPosition: item.pos || "center" }}
                  className="h-full w-full object-cover brightness-[1.08] contrast-[1.07] saturate-[1.4] transition-transform duration-700 ease-out group-hover:scale-105"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0908]/85 via-transparent to-transparent opacity-55 transition-opacity duration-500 group-hover:opacity-85" />
              <div
                aria-hidden="true"
                className="absolute inset-0 bg-gradient-to-tr from-ember/15 via-transparent to-gold/10 opacity-0 mix-blend-overlay transition-opacity duration-500 group-hover:opacity-100"
              />
              <Plus className="absolute right-4 top-4 size-5 text-amber-200 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              <div className="absolute bottom-0 left-0 p-3 sm:p-5">
                <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-amber-500/90 sm:text-[10px]">
                  {item.category}
                </p>
                <h3 className="mt-1 font-serif text-sm leading-snug text-amber-50 sm:text-xl">{item.title}</h3>
              </div>
            </motion.button>
          ))}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {current && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[65] flex flex-col bg-[#0A0908]/97 p-4 backdrop-blur-md sm:p-8"
            onClick={() => setLightbox(null)}
          >
            <button
              data-testid="gallery-lightbox-close"
              aria-label="Close lightbox"
              onClick={() => setLightbox(null)}
              className="absolute right-5 top-5 z-10 border border-neutral-700 bg-[#0A0908]/60 p-2 text-amber-100 transition-colors hover:border-amber-500"
            >
              <X className="size-5" />
            </button>
            <div
              className="flex flex-1 items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid="gallery-lightbox-prev"
                aria-label="Previous image"
                onClick={() => setLightbox((c) => (c - 1 + items.length) % items.length)}
                className="mr-3 hidden border border-neutral-700 p-2 text-amber-100 transition-colors hover:border-amber-500 sm:block"
              >
                <ChevronLeft className="size-5" />
              </button>
              <motion.img
                key={current.id}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.35 }}
                src={current.url}
                alt={current.title}
                className="max-h-[72vh] max-w-full border border-neutral-800 object-contain brightness-[1.08] saturate-[1.3]"
              />
              <button
                data-testid="gallery-lightbox-next"
                aria-label="Next image"
                onClick={() => setLightbox((c) => (c + 1) % items.length)}
                className="ml-3 hidden border border-neutral-700 p-2 text-amber-100 transition-colors hover:border-amber-500 sm:block"
              >
                <ChevronRight className="size-5" />
              </button>
            </div>
            <div
              className="mx-auto mt-4 max-w-2xl text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500/90">
                {current.category}
              </p>
              <h3 className="mt-1 font-serif text-2xl text-amber-50">{current.title}</h3>
              {current.description && (
                <p className="mt-2 text-sm text-neutral-400">{current.description}</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
